import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import CloudUploadIcon from 'mdi-material-ui/CloudUploadOutline';

import Button from 'components/Button/Button';
import Cropper from 'components/ImageCrop/Cropper';

const useStyles = makeStyles((theme) => ({
  root: {},
  initUploadButton: {
    border: `1px dashed ${theme.palette.text.tertiary}`,
    borderRadius: theme.spacing(0.5),
    color: theme.palette.text.tertiary,
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  initUploadIcon: {
    color: 'inherit',
  },
  imageContainer: {
    display: 'flex',
  },
  imageGrid: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    maxWidth: 120,
    maxHeight: 120,
    height: '100%',
  },
  loading: {
    ...theme.component.dashboardButtonIcon,
  },
  buttonsGrid: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 320,
  },
  button: {
    marginBottom: theme.spacing(0.5),
  },
}));

function ProfileImage({
  src,
  loading,
  onCroppedImageSave,
  onProfileImageChange,
  setProfileImageModified,
  onProfileImageRotate,
  onProfileImageRemove,
  isDeleteAllowed = true,
}) {
  const classes = useStyles();
  const imageInput = useRef(null);
  const [showCropper, setShowCropper] = useState(false);

  const { t } = useTranslation('common');

  const saveCroppedImage = async (file) => {
    await onCroppedImageSave(file);
    setShowCropper(false);
  };

  return (
    <div className={classes.root}>
      <input
        accept="image/*"
        className={classes.input}
        id="upload-profile-image-input"
        onChange={onProfileImageChange}
        style={{ display: 'none' }}
        type="file"
        ref={imageInput}
      />
      {loading ? (
        <CircularProgress className={classes.loading} />
      ) : !src ? (
        <ButtonBase
          className={classes.initUploadButton}
          onClick={() => imageInput.current.click()}
        >
          <CloudUploadIcon className={classes.initUploadIcon} />
          <Typography color="inherit" variant="body2">
            {t('profile.picture_upload')}
          </Typography>
        </ButtonBase>
      ) : (
        <Grid container spacing={1} className={classes.imageContainer}>
          {showCropper && (
            <Grid item xs={12} md={6}>
              <Cropper
                onSaveCroppedImage={saveCroppedImage}
                source={src}
                s3Object={src ? false : true}
                setProfileImageModified={setProfileImageModified}
              />
            </Grid>
          )}
          <Grid item xs={6} md={4} className={classes.imageGrid}>
            <Avatar src={src} className={classes.image}></Avatar>
          </Grid>
          <Grid item xs={6} md={4} className={classes.buttonsGrid}>
            <Button
              color="primary"
              outline
              elevated
              size="small"
              className={classes.button}
              onClick={() => imageInput.current.click()}
            >
              {t('profile.picture_upload')}
            </Button>
            <Button
              color="primary"
              outline
              elevated
              size="small"
              className={classes.button}
              onClick={() => onProfileImageRotate(src)}
            >
              {t('profile.rotate')}
            </Button>
            <Button
              //TODO: Implement better cropping tool
              color="primary"
              outline
              elevated
              size="small"
              className={classes.button}
              //style={{ display: 'none' }}
              onClick={() => setShowCropper(true)}
            >
              {t('profile.crop_image')}
            </Button>

            <Button
              color="primary"
              outline
              elevated
              size="small"
              className={classes.button}
              onClick={() => onProfileImageRemove()}
              disabled={!isDeleteAllowed}
            >
              {t('profile.picture_remove')}
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
}

ProfileImage.propTypes = {
  loading: PropTypes.bool,
  onProfileImageRemove: PropTypes.func,
  onProfileImageRotate: PropTypes.func,
  src: PropTypes.string,
  onCroppedImageSave: PropTypes.func,
};

export default ProfileImage;
